import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

export interface User {
  id: string;
  username: string;
  discriminator: string;
  avatar: string | null;
  email?: string;
}

export interface Guild {
  id: string;
  name: string;
  icon: string | null;
  owner: boolean;
  permissions: string;
  features: string[];
}

interface AuthContextType {
  user: User | null;
  guilds: Guild[];
  accessToken: string | null;
  yahooAccessToken: string | null;
  login: (user: User, guilds: Guild[], accessToken: string) => void;
  loginYahoo: (accessToken?: string) => void;
  logout: () => void;
  isAuthenticated: boolean;
  isYahooAuthenticated: boolean;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [guilds, setGuilds] = useState<Guild[]>([]);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [yahooAccessToken, setYahooAccessToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    // Check local storage for existing session
    const storedUser = localStorage.getItem('harambot_user');
    const storedGuilds = localStorage.getItem('harambot_guilds');
    const storedToken = localStorage.getItem('harambot_token');
    const storedYahooToken = localStorage.getItem('harambot_yahoo_token');

    if (storedUser && storedToken) {
      try {
        setUser(JSON.parse(storedUser));
        setAccessToken(storedToken);
        if (storedGuilds) {
          setGuilds(JSON.parse(storedGuilds));
        }
      } catch (e) {
        console.error('Failed to parse stored auth data', e);
        localStorage.removeItem('harambot_user');
        localStorage.removeItem('harambot_guilds');
        localStorage.removeItem('harambot_token');
      }
    }

    if (storedYahooToken && storedYahooToken !== 'undefined') {
      setYahooAccessToken(storedYahooToken);
    }

    setIsLoading(false);
  }, []);

  const login = useCallback((userData: User, guildData: Guild[], token: string) => {
    setUser(userData);
    setGuilds(guildData);
    setAccessToken(token);
    localStorage.setItem('harambot_user', JSON.stringify(userData));
    localStorage.setItem('harambot_guilds', JSON.stringify(guildData));
    localStorage.setItem('harambot_token', token);
  }, []);

  const loginYahoo = useCallback((token: string = 'true') => {
    setYahooAccessToken(token);
    localStorage.setItem('harambot_yahoo_token', token);
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setGuilds([]);
    setAccessToken(null);
    setYahooAccessToken(null);
    localStorage.removeItem('harambot_user');
    localStorage.removeItem('harambot_guilds');
    localStorage.removeItem('harambot_token');
    localStorage.removeItem('harambot_yahoo_token');
  }, []);

  return (
    <AuthContext.Provider value={{ 
      user, 
      guilds, 
      accessToken, 
      yahooAccessToken,
      login, 
      loginYahoo,
      logout, 
      isAuthenticated: !!user, 
      isYahooAuthenticated: !!yahooAccessToken,
      isLoading 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
